import { format } from 'date-fns';
import type { IPhoneCall } from '../../../extViewmodels';
import { UI_VERSION_FOCUSED, UI_VERSION_LEGACY } from '../../focused-view/useFocusedViewEnabled';

/**
 * Display helpers for a coaching conversation's linked `IPhoneCall`. Shared by the list rows
 * (`ConversationList`) and the detail metadata (`MetadataPanel`) so the two surfaces render call
 * info identically.
 */

/**
 * Humanizes the call's stamped `uiVersion`: `'focused-view'` -> `Focused view`, `'legacy'` ->
 * `Legacy`. Falls back to an em dash when there's no value, or to the raw value for any unrecognized
 * non-empty tag.
 */
export const formatUiVersion = (value?: string): string => {
	if (value === UI_VERSION_FOCUSED) {
		return 'Focused view';
	}
	if (value === UI_VERSION_LEGACY) {
		return 'Legacy';
	}
	return value?.trim() || '—';
};

/** True when the call actually connected — derived from the backend-stamped `milestones.connected`. */
export const isCallAnswered = (phoneCall?: IPhoneCall | null): boolean => Boolean(phoneCall?.milestones?.connected);

/** Human label for the call's outcome/status (e.g. `"Completed"`); em dash when unknown. */
export const formatCallStatus = (value?: string): string => value?.trim() || '—';

/**
 * Formats a call's connected duration (the backend stamps `duration` in milliseconds) as `m:ss`,
 * e.g. `185000` -> `"3:05"`. Returns null for missing/zero/invalid durations so callers can omit it
 * (a call that never connected has no meaningful talk time to show).
 */
export const formatCallDuration = (durationMs?: number): string | null => {
	if (durationMs == null || !Number.isFinite(durationMs) || durationMs <= 0) {
		return null;
	}
	const totalSeconds = Math.round(durationMs / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const formatCallTimestamp = (value?: string): string => {
	if (!value) {
		return 'Unknown start time';
	}
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return 'Unknown start time';
	}
	return format(date, "MMM d, yyyy 'at' h:mm a");
};
